import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MdAdd, MdError } from 'react-icons/md';
import RecordCard from '../../components/StudentRecords/RecordCard';
import CreateRecordModal from '../../components/StudentRecords/CreateRecordModal';
import AddFollowUpModal from '../../components/StudentRecords/AddFollowUpModal';
import RecordFilters from '../../components/StudentRecords/RecordFilters';
import { useStudentRecords } from '../../hooks/useStudentRecords';
import useImpersonation from '../../hooks/useImpersonation';
import useTheme from '../../context/ThemeContext';
import * as studentService from '../../services/studentService';
import useClubVocab from '../../hooks/useClubVocab';

// Normalize student response (handles multiple possible API response shapes)
const normalizeStudents = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.students)) return payload.students;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

function StudentRecords() {
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;
  const v = useClubVocab();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpRecordId, setFollowUpRecordId] = useState(null);
  const [expandedRecords, setExpandedRecords] = useState({});
  const [filters, setFilters] = useState({
    studentId: '',
    recordType: '',
    category: '',
    severity: '',
    createdBy: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: 20,
  });

  // Get all students for dropdown (same as Students page, no role filter)
  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: ['students-dropdown', { page: 1, limit: 100 }],
    queryFn: () => {
      return studentService.getStudents({ page: 1, limit: 100 }).then(response => {
        const rawData = response.data;
        console.log('[StudentRecords] Raw API response:', rawData);
        const normalized = normalizeStudents(rawData);
        console.log('[StudentRecords] Normalized students:', normalized);
        return rawData; // Return the raw data, normalize below
      });
    },
  });

  // Extract students using same normalization as Students page
  const students = normalizeStudents(studentsData);

  // Debug: show first student object structure
  if (students.length > 0) {
    console.log('[StudentRecords] First normalized student object:', students[0]);
  }

  // Get records
  const { data: recordsData, isLoading: recordsLoading, error: recordsError } = useStudentRecords(filters);

  const handleToggleExpand = useCallback((recordId) => {
    setExpandedRecords(prev => ({
      ...prev,
      [recordId]: !prev[recordId],
    }));
  }, []);

  const handleAddFollowUp = (recordId) => {
    setFollowUpRecordId(recordId);
    setShowFollowUpModal(true);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const records = recordsData?.records || [];
  const total = recordsData?.total || 0;
  const pages = recordsData?.pages || 1;
  const currentPage = filters.page;

  const hasActiveFilters = [
    filters.studentId,
    filters.recordType,
    filters.category,
    filters.severity,
    filters.createdBy,
    filters.dateFrom,
    filters.dateTo,
  ].filter(Boolean).length > 0;

  // Single source of truth for clearing filters — shared by the filter bar's
  // "Clear all" and the empty-state "Clear filters" so they behave identically.
  const clearFilters = () => setFilters({
    studentId: '', recordType: '', category: '', severity: '',
    createdBy: '', dateFrom: '', dateTo: '', page: 1, limit: 20,
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-background border-b border-divider sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div>
              <h1 className="text-2xl font-bold text-secondary">{v.member} Records</h1>
              <p className="text-sm text-muted mt-1">Progress notes and incident reports</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              disabled={isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              className={`flex items-center gap-2 px-4 py-2.5 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <MdAdd className="text-lg" /> Create Record
            </button>
          </div>
          <p className="text-xs text-muted">
            🔒 {v.member} records are locked after submission. Corrections can be added as follow-up notes.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border-b border-divider">
        <div className="max-w-3xl mx-auto px-4">
          <RecordFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            students={students}
            onClear={clearFilters}
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {recordsError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
            <MdError className="text-red-700 text-xl flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-900">
              Failed to load records. Please refresh the page.
            </p>
          </div>
        )}

        {/* Loading state */}
        {recordsLoading && (
          <div className="grid gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-card rounded-lg p-4 h-24 animate-pulse">
                <div className="h-full bg-nav-hover rounded"></div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!recordsLoading && records.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted text-lg mb-4">
              {hasActiveFilters ? 'No records match your filters' : `No ${v.member.toLowerCase()} records found`}
            </p>
            {hasActiveFilters && (
              <p className="text-sm text-muted mb-6">
                Try clearing filters or adjusting your search.
              </p>
            )}
            <button
              onClick={hasActiveFilters ? clearFilters : () => setShowCreateModal(true)}
              disabled={!hasActiveFilters && isReadOnly}
              title={!hasActiveFilters && isReadOnly ? 'Disabled: read-only support mode' : undefined}
              className="text-primary hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {hasActiveFilters ? 'Clear filters' : 'Create the first record'}
            </button>
          </div>
        )}

        {/* Records list */}
        {!recordsLoading && records.length > 0 && (
          <>
            <div className="grid gap-4 mb-6">
              {records.map(record => (
                <RecordCard
                  key={record._id}
                  record={record}
                  isExpanded={expandedRecords[record._id]}
                  onToggle={() => handleToggleExpand(record._id)}
                  onAddFollowUp={handleAddFollowUp}
                  canAddFollowUp={true}
                />
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-divider rounded-lg text-sm font-medium text-secondary hover:bg-nav-hover disabled:opacity-50 transition-colors"
                >
                  Previous
                </button>
                {Array.from({ length: pages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      page === currentPage
                        ? 'bg-secondary text-card'
                        : 'border border-divider text-secondary hover:bg-nav-hover'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(Math.min(pages, currentPage + 1))}
                  disabled={currentPage === pages}
                  className="px-3 py-2 border border-divider rounded-lg text-sm font-medium text-secondary hover:bg-nav-hover disabled:opacity-50 transition-colors"
                >
                  Next
                </button>
              </div>
            )}

            {/* Record count */}
            <p className="text-xs text-muted text-center mt-6">
              Showing {records.length} of {total} records
            </p>
          </>
        )}
      </div>

      {/* Modals */}
      <CreateRecordModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        students={students}
        isLoadingStudents={studentsLoading}
        onSuccess={() => {
          setFilters(prev => ({ ...prev, page: 1 }));
        }}
      />

      {followUpRecordId && (
        <AddFollowUpModal
          isOpen={showFollowUpModal}
          onClose={() => {
            setShowFollowUpModal(false);
            setFollowUpRecordId(null);
          }}
          recordId={followUpRecordId}
        />
      )}
    </div>
  );
}

export default StudentRecords;
